import type { FitPreference } from '@nfa/types';

export interface ConversionOptions {
  modifier?: 'budget' | 'ultralight' | 'premium';
  fit?: FitPreference;
}

export interface KitConversionsProps {
  query: string;
  currentModifier?: 'budget' | 'ultralight' | 'premium';
  currentFit: FitPreference;
  currentTotals: { price_gbp: number; weight_g: number };
  onConvert: (options: ConversionOptions) => void;
  isConverting: boolean;
  convertingTo?: ConversionOptions;
}

const MODIFIER_BUTTONS: {
  modifier: 'budget' | 'ultralight' | 'premium';
  label: string;
  icon: string;
  description: string;
}[] = [
  { modifier: 'budget', label: 'Budget', icon: '💰', description: 'More affordable options' },
  { modifier: 'ultralight', label: 'Ultralight', icon: '⚡', description: 'Lighter weight gear' },
  { modifier: 'premium', label: 'Premium', icon: '✨', description: 'Top-tier quality' },
];

const FIT_BUTTONS: {
  fit: FitPreference;
  label: string;
  icon: string;
}[] = [
  { fit: 'womens', label: "Women's Fit", icon: '♀' },
  { fit: 'mens', label: "Men's Fit", icon: '♂' },
];

export function KitConversions({
  currentModifier,
  currentFit,
  onConvert,
  isConverting,
  convertingTo,
}: KitConversionsProps) {
  return (
    <div className="p-4 bg-neutral-900/50 border border-neutral-800 rounded-xl">
      <p className="text-sm text-neutral-400 mb-3">Try a different style:</p>

      <div className="flex flex-wrap gap-2">
        {/* Modifier Buttons */}
        {MODIFIER_BUTTONS.map(({ modifier, label, icon }) => {
          const isCurrent = currentModifier === modifier;
          const isThisConverting =
            isConverting && convertingTo?.modifier === modifier;

          return (
            <button
              key={modifier}
              onClick={() => onConvert({ modifier })}
              disabled={isCurrent || isConverting}
              className={`px-3 py-2 text-sm font-medium rounded-lg border transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                isCurrent
                  ? 'bg-orange-500/20 border-orange-500 text-orange-400'
                  : 'bg-neutral-800 border-neutral-700 text-neutral-300 hover:border-neutral-500 hover:text-white'
              }`}
              title={isCurrent ? 'Current selection' : `Switch to ${label}`}
            >
              {isThisConverting ? (
                <svg
                  className="animate-spin h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              ) : (
                <span>{icon}</span>
              )}
              <span>{label}</span>
            </button>
          );
        })}

        {/* Separator */}
        <div className="w-px bg-neutral-700 mx-1 self-stretch" />

        {/* Fit Buttons */}
        {FIT_BUTTONS.map(({ fit, label, icon }) => {
          const isCurrent = currentFit === fit;
          const isThisConverting = isConverting && convertingTo?.fit === fit;

          return (
            <button
              key={fit}
              onClick={() => onConvert({ fit })}
              disabled={isCurrent || isConverting}
              className={`px-3 py-2 text-sm font-medium rounded-lg border transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                isCurrent
                  ? 'bg-orange-500/20 border-orange-500 text-orange-400'
                  : 'bg-neutral-800 border-neutral-700 text-neutral-300 hover:border-neutral-500 hover:text-white'
              }`}
              title={isCurrent ? 'Current selection' : `Switch to ${label}`}
            >
              {isThisConverting ? (
                <svg
                  className="animate-spin h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              ) : (
                <span>{icon}</span>
              )}
              <span>{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
